import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, UserPlus, Users, Archive } from "lucide-react";
import { useData } from "../context/DataContext";
import StudentCard from "../components/StudentCard";
import EmptyState from "../components/EmptyState";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

const FILTERS = ["All", "Local", "Regional", "International"];

export default function Students() {
    const { students, archivedStudents, attendance } = useData();
    const [query, setQuery] = useState("");
    const [filter, setFilter] = useState("All");
    const [view, setView] = useState("active"); // "active" | "archived"

    const source = view === "active" ? students : archivedStudents;

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return source.filter((s) => {
            const matchesType = filter === "All" || s.type === filter;
            if (!matchesType) return false;
            if (!q) return true;
            return (
                s.name.toLowerCase().includes(q) ||
                s.instrument.toLowerCase().includes(q)
            );
        });
    }, [source, query, filter]);

    const hasArchive = archivedStudents.length > 0;

    return (
        <div className="space-y-6" data-testid="students-page">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
                <div>
                    <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500 mb-1">
                        Roster
                    </div>
                    <h1 className="font-display font-bold text-4xl sm:text-5xl uppercase tracking-tight">
                        Students
                    </h1>
                </div>
                {view === "active" && (
                    <Link to="/students/new" data-testid="students-add-btn">
                        <Button className="rounded-xl bg-[#3D6385] hover:bg-[#345570] text-white font-bold h-11 px-5">
                            <UserPlus size={16} strokeWidth={2.8} className="mr-2" />
                            New Student
                        </Button>
                    </Link>
                )}
            </div>

            {/* Tabs: Active / Archived */}
            {hasArchive && (
                <div
                    className="inline-flex p-1 rounded-2xl bg-mist"
                    data-testid="students-view-tabs"
                >
                    <button
                        onClick={() => setView("active")}
                        data-testid="students-view-active"
                        aria-pressed={view === "active"}
                        className={`focus-ring px-4 h-10 rounded-xl text-sm font-bold inline-flex items-center gap-2 transition-all ${
                            view === "active"
                                ? "bg-white text-slate-900 shadow-sm"
                                : "text-slate-500 hover:text-slate-700"
                        }`}
                    >
                        <Users size={14} strokeWidth={2.8} />
                        Active
                        <span className="text-xs font-bold opacity-60 tabular-nums">
                            {students.length}
                        </span>
                    </button>
                    <button
                        onClick={() => setView("archived")}
                        data-testid="students-view-archived"
                        aria-pressed={view === "archived"}
                        className={`focus-ring px-4 h-10 rounded-xl text-sm font-bold inline-flex items-center gap-2 transition-all ${
                            view === "archived"
                                ? "bg-white text-slate-900 shadow-sm"
                                : "text-slate-500 hover:text-slate-700"
                        }`}
                    >
                        <Archive size={14} strokeWidth={2.8} />
                        Archived
                        <span className="text-xs font-bold opacity-60 tabular-nums">
                            {archivedStudents.length}
                        </span>
                    </button>
                </div>
            )}

            <div className="bg-white rounded-3xl border border-mist card-soft p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="relative flex-1">
                    <Search
                        size={18}
                        strokeWidth={2.5}
                        className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                    />
                    <Input
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search by name or instrument…"
                        className="pl-11 h-11 rounded-xl bg-mist/50 border-mist focus-visible:ring-[#3D6385]"
                        data-testid="student-search-input"
                    />
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    {FILTERS.map((f) => (
                        <button
                            key={f}
                            onClick={() => setFilter(f)}
                            data-testid={`filter-${f.toLowerCase()}`}
                            aria-pressed={filter === f}
                            className={`focus-ring px-4 h-11 rounded-xl text-sm font-bold transition-all ${
                                filter === f
                                    ? "bg-ink text-white"
                                    : "bg-mist/50 text-slate-600 hover:bg-mist"
                            }`}
                        >
                            {f}
                        </button>
                    ))}
                </div>
            </div>

            {source.length === 0 ? (
                view === "archived" ? (
                    <EmptyState
                        icon={Archive}
                        title="Nothing archived"
                        message="When you archive a student, they'll appear here so you can restore them later."
                    />
                ) : (
                    <EmptyState
                        icon={Users}
                        title="No students yet"
                        message="Add your first student to start tracking lessons and progress."
                        action={
                            <Link
                                to="/students/new"
                                data-testid="empty-add-student-btn"
                            >
                                <Button className="rounded-xl bg-[#3D6385] hover:bg-[#345570] text-white font-bold h-11 px-6">
                                    <UserPlus
                                        size={16}
                                        strokeWidth={2.8}
                                        className="mr-2"
                                    />
                                    Add Student
                                </Button>
                            </Link>
                        }
                    />
                )
            ) : filtered.length === 0 ? (
                <EmptyState
                    icon={Search}
                    title="No matches"
                    message="Try a different name, instrument, or filter."
                />
            ) : (
                <div
                    className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
                    data-testid={
                        view === "active" ? "students-grid" : "students-grid-archived"
                    }
                >
                    {filtered.map((s, i) => (
                        <div
                            key={s.id}
                            className="fade-up relative"
                            style={{ animationDelay: `${i * 40}ms` }}
                        >
                            {view === "archived" && (
                                <span className="absolute top-3 right-3 z-10 inline-flex items-center gap-1 bg-warning-soft text-warning text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full shadow-sm">
                                    <Archive size={10} strokeWidth={3} />
                                    Archived
                                </span>
                            )}
                            <StudentCard student={s} attendance={attendance} />
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
